<template>
  <div class="material-preparation-container">
    <div 
      v-if="showNotice" 
      class="notice-popup"
      :class="`notice-level-${noticeData.emergencyLevel}`"
    >
      <div class="notice-icon">⚠️</div>
      <div class="notice-content">
        <div class="notice-title">
          {{ noticeData.emergencyLevel === 1 && noticeData.message.includes('新备料任务') ? '新备料任务提醒' : '物料紧急程度更新' }}
        </div>
        <div class="notice-desc">{{ noticeData.message }}</div>
      </div>
    </div>

    <header class="header">
      <div class="back-button" @click="goBack">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon">
      </div>
      <h1 class="page-title">备料管理</h1>
      <!-- 声音开关图标 -->
      <div class="header-right">
        <div 
          class="sound-toggle" 
          @click="toggleSound"
          :title="isSoundEnabled ? '关闭提示音' : '开启提示音'"
        >
          <span v-if="isSoundEnabled">🔊</span>
          <span v-else>🔇</span>
        </div>
      </div>
    </header>
    
    <main class="content">
      <!-- 音频权限提示（仅声音开启且未初始化时显示） -->
      <div v-if="!isAudioInited && isSoundEnabled" class="audio-permission-tip" @click="handlePageClick">
        🔔 点击页面激活消息提示音，紧急通知将同步发声提醒
      </div>
      <!-- 备料单列表 -->
      <div class="order-list">
        <div class="filter-nav">
          <div 
            v-for="filter in filterOptions" 
            :key="filter.value"
            :class="['filter-item', { active: activeFilter === filter.value }]"
            @click="setFilter(filter.value)"
          >
            {{ filter.label }}
          </div>
        </div>
        
        <!-- 紧急状态筛选 -->
        <div class="filter-nav emergency-filter">
          <div 
            v-for="filter in emergencyFilterOptions" 
            :key="filter.value"
            :class="['filter-item', { active: activeEmergencyFilter === filter.value }]"
            @click="setEmergencyFilter(filter.value)"
          >
            {{ filter.label }}
          </div>
        </div>

        <div class="search-container">
          <select 
            v-model="searchType" 
            class="search-type-select"
          >
            <option value="orderNo">备料单号</option>
            <option value="supplierName">供应商</option>
            <option value="materialNo">物料编码</option>
          </select>
          <input 
            type="text" 
            v-model="searchKeyword" 
            :placeholder="getSearchPlaceholder()" 
            class="search-input"
            @keyup.enter="searchOrders"
          >
          <button class="search-btn" @click="searchOrders">搜索</button>
        </div>
        
        <div class="order-items">
          <div 
            v-for="order in orders" 
            :key="order.id"
            class="order-item"
            @click="toggleOrderDetail(order.id)"
          >
            <div class="order-header">
              <div class="order-number">
                {{ order.supplierName }}
                <span 
                  v-if="order.emergencyLevel && order.emergencyLevel >= 2"
                  :class="['emergency-badge', `emergency-${order.emergencyLevel}`]"
                  style="margin-left: 8px; padding: 2px 6px; border-radius: 4px; font-size: 12px; font-weight: 600;"
                >
                  {{ getEmergencyLevelText(order.emergencyLevel) }}
                </span>
              </div>
              <div :class="['order-status', `status-${order.planStatus}`]">{{ getStatusText(order.planStatus) }}</div>
            </div>
            
            <div class="order-info">
              <div class="info-row">
                <div class="info-item left">
                  <span class="label">订单号：</span>
                  <span class="value">{{ order.orderNo }}</span>
                </div>
              </div> 
              <div class="info-row">
                <div class="info-item left">
                  <span class="label">物料编码：</span>
                  <span class="value">{{ order.materialNo }}</span>
                </div>
              </div>
              <div class="info-row">
                <div class="info-item left">
                  <span class="label">备料数量：</span>
                  <span class="value">{{ order.planNum }}</span>
                </div>
                <div class="info-item right">
                  <span class="value">{{ order.boxNum }}</span>
                </div>
              </div>
            </div>
            
            <!-- 展开/收起图标 -->
            <div class="expand-icon" :class="{ expanded: expandedOrderId === order.id }">
              ▼
            </div>
            
            <!-- 展开后的详情内容 -->
            <div v-if="expandedOrderId === order.id" class="order-detail">
              <!-- 备料单详情 -->
              <div class="detail-section">
                <h3>备料单详情</h3>
                <div class="detail-info">
                  <div class="info-item">
                    <span class="label">计划版本：</span>
                    <span class="value">{{ order.version }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">实际备料：</span>
                    <span class="value">{{ order.actualNum || '-' }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">计划单号：</span>
                    <span class="value">{{ order.planNo }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">物料描述：</span>
                    <span class="value">{{ order.materialDesc }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">需求日期：</span>
                    <span class="value">{{ order.demandTime }}</span>
                  </div>
                  <div class="info-item">
                    <span class="label">备注：</span>
                    <span class="value">{{ order.planRemark || '-' }}</span>
                  </div>
                </div>
              </div>
              
              <!-- 操作按钮区域 -->
              <div class="action-buttons">
                <!-- 第一行：分配人员和取消备料 -->
                <div class="btn-row">
                  <button 
                    v-if="order.planStatus === 1 || order.planStatus === 2 || order.planStatus === 3" 
                    class="btn btn-primary"
                    @click.stop="openDistributeModal(order)"
                  >
                    分配人员
                  </button>
                  <button 
                    v-if="canOperate(order)" 
                    class="btn btn-danger"
                    @click.stop="cancelPreparation(order.id)"
                  >
                    取消备料
                  </button>
                </div>
                
                <!-- 第二行：完成备料 -->
                <div class="btn-row">
                  <button 
                    v-if="order.planStatus === 3" 
                    class="btn btn-success"
                    @click.stop="completePreparation(order)"
                  >
                    完成备料
                  </button>
                </div>
                
                <!-- 第三行：开始备料 -->
                <div class="btn-row">
                  <button 
                    v-if="order.planStatus === 1 || order.planStatus === 2" 
                    class="btn btn-primary"
                    @click.stop="startPreparation(order.id)"
                  >
                    开始备料
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 无数据提示 -->
        <div v-if="orders.length === 0 && !loading" class="no-data">
          暂无备料单数据
        </div>
        
        <!-- 加载中提示 -->
        <div v-if="loading" class="loading">
          加载中...
        </div>
        
        <!-- 分页组件 -->
        <div v-if="total > 0" class="pagination">
          <div class="pagination-info">
            共 {{ total }} 条记录，第 {{ currentPage }} / {{ totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button 
              class="page-btn" 
              :disabled="currentPage === 1" 
              @click="handlePageChange(currentPage - 1)"
            >
              上一页
            </button>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages" 
              @click="handlePageChange(currentPage + 1)"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
  
  <!-- 数量输入弹窗 -->
  <div v-if="showQuantityModal" class="modal-overlay" @click.self="closeQuantityModal">
    <div class="quantity-modal">
      <div class="modal-header">
        <h3>确认完成备料</h3>
        <button class="close-btn" @click="closeQuantityModal">×</button>
      </div>
      <div class="modal-body">
        <div v-if="selectedOrder" class="order-info">
          <div class="info-item">
            <span class="label">订单号：</span>
            <span class="value">{{ selectedOrder.orderNo }}</span>
          </div>
          <div class="info-item">
            <span class="label">物料编码：</span>
            <span class="value">{{ selectedOrder.materialNo }}</span>
          </div>
          <div class="info-item">
            <span class="label">物料描述：</span>
            <span class="value">{{ selectedOrder.materialDesc }}</span>
          </div>
          <div class="info-item">
            <span class="label">计划备料数量：</span>
            <span class="value">{{ selectedOrder.planNum }}</span>
          </div>
          <div class="info-item quantity-input-group">
            <span class="label">实际备料数量：</span>
            <input 
              type="number" 
              v-model.number="actualQuantity" 
              :disabled="!canModifyQuantity"
              min="1"
              class="quantity-input"
            >
            <span v-if="!canModifyQuantity" class="warning-text">（库存不足，无法修改）</span>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-default" @click="closeQuantityModal">取消</button>
        <button class="btn btn-success" @click="confirmCompletePreparation">确认完成</button>
      </div>
    </div>
  </div>

  <!-- 分配人员弹窗 -->
  <div v-if="showDistributeModal" class="modal-overlay" @click.self="closeDistributeModal">
    <div class="distribute-modal">
      <div class="modal-header">
        <h3>分配备料任务</h3>
        <button class="close-btn" @click="closeDistributeModal">×</button>
      </div>
      <div class="modal-body">
        <div v-if="currentDistributeOrder" class="order-info">
          <div class="info-item">
            <span class="label">订单号：</span>
            <span class="value">{{ currentDistributeOrder.orderNo }}</span>
          </div>
          <div class="info-item">
            <span class="label">物料编码：</span>
            <span class="value">{{ currentDistributeOrder.materialNo }}</span>
          </div>
          <div class="info-item">
            <span class="label">物料描述：</span>
            <span class="value">{{ currentDistributeOrder.materialDesc }}</span>
          </div>
        </div>
        <div class="users-list">
          <h4>选择分配人员：</h4>
          <div v-if="loadingUsers" class="loading">加载人员列表中...</div>
          <div v-else-if="users.length === 0" class="no-data">暂无人员数据</div>
          <div v-else>
            <div 
              v-for="user in users" 
              :key="user.id"
              :class="['user-item', { selected: selectedUserId === user.id }]"
              @click="selectUser(user.id)"
            >
              <div class="user-name">{{ user.name }}</div>
              <div class="user-name">{{ user.account }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-default" @click="closeDistributeModal">取消</button>
        <button 
          class="btn btn-primary" 
          @click="confirmDistribute" 
          :disabled="!selectedUserId"
        >
          确认分配
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { searchMaterialPreparationOrders, updateMaterialPreparationStatus, getUsersList, distributePreparationTask } from '@/api/materialPreparation.js'
import toast from '@/utils/toast.js'
import { getUserId } from '@/utils/auth.js'

export default {
  name: 'MaterialPreparation',
  setup() {
    const router = useRouter()
    const orders = ref([])
    const loading = ref(false)
    const searchKeyword = ref('')
    const searchType = ref('orderNo')
    const expandedOrderId = ref(null)
    const activeFilter = ref(0) // 0: 全部, 1: 待备料, 4: 备料完成, 6: 已取消
    const activeEmergencyFilter = ref(0) // 0: 全部, 1: 正常, 2: 紧急, 3: 特急
    // 分页相关变量
    const currentPage = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const filterOptions = [
      { label: '全部', value: 0 },
      { label: '备料中', value: 3 },
      { label: '完成', value: 4 },
      { label: '已取消', value: 6 }
    ]
    const emergencyFilterOptions = [
      { label: '全部', value: 0 },
      { label: '正常', value: 1 },
      { label: '紧急', value: 2 },
      { label: '特急', value: 3 }
    ]

    // WebSocket 相关变量
    const ws = ref(null)
    const isWsConnected = ref(false)
    // 消息通知变量
    const showNotice = ref(false)
    const noticeData = ref({
      emergencyLevel: 2,
      message: '',
      materialNo: '' // 新增：存储物料编码
    })
    // 音频相关变量
    const isAudioInited = ref(false)
    const isSoundEnabled = ref(false)
    const hasUserInteracted = ref(false)

    // 切换声音开关状态时，同时存储到localStorage
    const toggleSound = () => {
      isSoundEnabled.value = !isSoundEnabled.value;
      toast.default(isSoundEnabled.value ? '提示音已开启' : '提示音已关闭');
      // 存储状态到localStorage
      localStorage.setItem('isSoundEnabled', JSON.stringify(isSoundEnabled.value));
      
      if (isSoundEnabled.value && !isAudioInited.value) {
        initNoticeAudio();
      }
    }

    // 初始化语音播报
    const initNoticeAudio = () => {
      // 确保只有在声音开启时才执行初始化
      if (!isSoundEnabled.value) {
        return;
      }
      
      if (!('speechSynthesis' in window)) {
        toast.error('当前浏览器不支持语音提示，请更换浏览器');
        return;
      }

      const testSpeech = new SpeechSynthesisUtterance();
      testSpeech.text = '';
      testSpeech.volume = 0;
      window.speechSynthesis.speak(testSpeech);

      isAudioInited.value = true;
      hasUserInteracted.value = true;
      console.log('手机端语音播报初始化完成');
    }

    // 全局用户交互监听
    const handleGlobalInteraction = () => {
      if (!hasUserInteracted.value) {
        hasUserInteracted.value = true;
        if (isSoundEnabled.value && !isAudioInited.value) {
          initNoticeAudio();
        }
      }
      document.removeEventListener('click', handleGlobalInteraction);
      document.removeEventListener('touchstart', handleGlobalInteraction);
    }

    // 语音播报核心逻辑（新增物料编码播报）
    const playNoticeSound = (materialNo, level) => {
      if (!isAudioInited.value) return;

      let text = '';
      // 根据物料编码区分是新备料任务还是紧急程度变更
      if (materialNo === '新任务') {
        // 新物料任务
        text = '有新物料编码需要备料';
      } else {
        // 紧急程度变更
        const voiceTextMap = {
          1: '有物料编码修改正常',
          2: '有物料编码修改紧急',
          3: '有物料编码修改特急'
        };
        text = voiceTextMap[level] || '物料状态更新';
      }

      const speech = new SpeechSynthesisUtterance();
      speech.text = text;
      speech.volume = 1;
      speech.rate = 1.3;
      speech.pitch = 1.2;
      speech.lang = 'zh-CN';

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(speech);
    }

    // 显示消息通知弹窗（传递物料编码）
    const showNoticePopup = (msg, level, materialNo) => {
      noticeData.value = { 
        emergencyLevel: level, 
        message: msg,
        materialNo: materialNo // 存储物料编码
      };
      showNotice.value = true;

      if (isSoundEnabled.value) {
        if (isAudioInited.value) {
          playNoticeSound(materialNo, level); // 传入物料编码和紧急级别
        } else if (hasUserInteracted.value) {
          // 用户已经交互过，尝试初始化音频
          initNoticeAudio();
          // 立即播放声音，不等待初始化完成
          playNoticeSound(materialNo, level);
        } else {
          // 用户还没有交互过，尝试进行自动交互以激活音频
          handleGlobalInteraction();
          // 初始化音频
          initNoticeAudio();
          // 立即播放声音
          playNoticeSound(materialNo, level);
        }
      }

      setTimeout(() => showNotice.value = false, 10000);
    }

    // 初始化 WebSocket 连接（解析消息中的物料编码）
    const initWebSocket = () => {
      console.log('尝试初始化WebSocket连接，正在获取用户ID');
      const userId = getUserId() || '';
      console.log('获取到的用户ID:', userId);
      if (!userId) {
        console.warn('未获取到用户ID，WebSocket 连接失败');
        return;
      }

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/simple?userId=${userId}`;
      ws.value = new WebSocket(wsUrl);

      ws.value.onopen = () => {
        console.log('WebSocket 连接成功');
        isWsConnected.value = true;
        // 连接成功后，如果声音是开启的，确保音频已初始化
        if (isSoundEnabled.value && !isAudioInited.value && hasUserInteracted.value) {
          initNoticeAudio();
        }
      };

      ws.value.onmessage = (event) => {
        const message = event.data;
        console.log('收到实时消息：', message);

        if (message.includes('物料紧急程度更新')) {
          let emergencyLevel = 2;
          if (message.includes('特急')) emergencyLevel = 3;
          if (message.includes('正常')) emergencyLevel = 1;

          // 从消息中提取物料编码（假设消息格式包含"物料编码：XXX"）
          // 若实际消息格式不同，需根据真实格式调整正则
          const materialNoMatch = message.match(/物料编码：(\S+)/);
          const materialNo = materialNoMatch ? materialNoMatch[1] : '未知编码';

          showNoticePopup(message, emergencyLevel, materialNo); // 传入物料编码
          fetchOrders();
        } else if (message.includes('【新备料任务】')) {
          // 处理新备料任务消息
          // 移除紧急程度相关逻辑，仅显示新备料任务
          const emergencyLevel = 1; // 默认使用正常级别
          // 由于后端消息不包含具体物料编码，使用"新任务"作为标识
          const materialNo = '新任务';
          
          // 自定义新任务提示文案，突出"新任务"而非"正常"
          const noticeMsg = `您有新的备料任务待处理：${message.replace('【新备料任务】', '')}`;
          showNoticePopup(noticeMsg, emergencyLevel, materialNo);
          fetchOrders(); // 刷新订单列表，显示新分配的任务
        }else if (message.includes('【备料任务取消】')) {
          const emergencyLevel = 3; 
          const materialNo = '取消物料';

          const noticeMsg = `您有新的取消物料任务待处理：${message.replace('【备料任务取消】', '')}`;
          showNoticePopup(noticeMsg, emergencyLevel, materialNo);
          fetchOrders(); 
        }
      };

      ws.value.onclose = () => {
        console.log('WebSocket 连接关闭，3秒后重试');
        setTimeout(initWebSocket, 3000);
      };

      ws.value.onerror = (error) => {
        console.error('WebSocket 连接错误：', error);
        isWsConnected.value = false;
      };
    }

    // 设置紧急状态筛选（不变）
    const setEmergencyFilter = (value) => {
      activeEmergencyFilter.value = value;
      currentPage.value = 1;
      fetchOrders();
    }

    // 分页方法（不变）
    const handlePageChange = (page) => {
      currentPage.value = page;
      fetchOrders();
    }

    // 计算总页数（不变）
    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    })

    // 获取备料单列表（不变）
    const fetchOrders = async () => {
      loading.value = true;
      try {
        const params = {
          current: currentPage.value,
          size: pageSize.value,
          status: activeFilter.value
        };

        if (activeFilter.value !== 0) {
          params.statusList = activeFilter.value === 1 ? [1, 2] : [activeFilter.value];
        }

        if (activeEmergencyFilter.value !== 0) {
          params.emergencyLevel = activeEmergencyFilter.value;
        }

        const response = await searchMaterialPreparationOrders(params);
        if (response.code === 200 && response.data) {
          orders.value = Array.isArray(response.data.records) ? response.data.records : [];
          total.value = response.data.total || 0;
        } else {
          orders.value = [];
          total.value = 0;
          toast.error('获取备料单列表失败');
        }
      } catch (error) {
        console.error('获取备料单列表异常:', error);
        toast.error('系统错误，请重试');
        orders.value = [];
      } finally {
        loading.value = false;
      }
    }

    // 搜索备料单（不变）
    const searchOrders = async () => {
      currentPage.value = 1;
      loading.value = true;
      try {
        const params = {
          current: currentPage.value,
          size: pageSize.value,
          keyword: searchKeyword.value.trim(),
          searchType: searchType.value,
          status: activeFilter.value
        };

        if (activeFilter.value !== 0) {
          params.statusList = activeFilter.value === 1 ? [1, 2] : [activeFilter.value];
        }

        if (activeEmergencyFilter.value !== 0) {
          params.emergencyLevel = activeEmergencyFilter.value;
        }

        const response = await searchMaterialPreparationOrders(params);
        if (response.code === 200 && response.data) {
          orders.value = Array.isArray(response.data.records) ? response.data.records : [];
          total.value = response.data.total || 0;
        } else {
          orders.value = [];
          total.value = 0;
          toast.error('搜索失败');
        }
      } catch (error) {
        console.error('搜索异常:', error);
        toast.error('系统错误，请重试');
        orders.value = [];
      } finally {
        loading.value = false;
      }
    }

    // 设置筛选条件（不变）
    const setFilter = (filterValue) => {
      activeFilter.value = filterValue;
      searchKeyword.value = '';
      currentPage.value = 1;
      fetchOrders();
    }

    // 切换订单详情展开状态（不变）
    const toggleOrderDetail = (orderId) => {
      expandedOrderId.value = expandedOrderId.value === orderId ? null : orderId;
    }

    // 判断是否可操作订单（不变）
    const canOperate = (order) => {
      return [1, 2, 3, 4].includes(order.planStatus);
    }

    // 开始备料（不变）
    const startPreparation = async (id) => {
      try {
        const response = await updateMaterialPreparationStatus(id, 3);
        if (response.code === 200) {
          toast.success('开始备料成功');
          fetchOrders();
        } else {
          toast.error('开始备料失败：' + (response.msg || '未知错误'));
        }
      } catch (error) {
        console.error('开始备料异常:', error);
        toast.error('系统错误，请重试');
      }
    }

    // 弹窗相关数据
    const showQuantityModal = ref(false);
    const currentOrderId = ref(null);
    const selectedOrder = ref(null);
    const actualQuantity = ref(0);
    const canModifyQuantity = ref(true);
    
    // 分配人员相关数据
    const showDistributeModal = ref(false);
    const currentDistributeOrder = ref(null);
    const users = ref([]);
    const loadingUsers = ref(false);
    const selectedUserId = ref(null);
    
    // 打开数量输入弹窗
    const openQuantityModal = (order) => {
      currentOrderId.value = order.id;
      selectedOrder.value = order;
      actualQuantity.value = order.planNum; // 默认显示计划备料数量
      
      // 这里可以添加检查仓库库存的逻辑，根据实际情况设置canModifyQuantity
      // 目前假设库存充足，可以修改数量
      canModifyQuantity.value = true; // 实际应用中需要根据仓库库存判断
      
      showQuantityModal.value = true;
    }
    
    // 关闭弹窗
    const closeQuantityModal = () => {
      showQuantityModal.value = false;
      currentOrderId.value = null;
      selectedOrder.value = null;
      actualQuantity.value = 0;
    }
    
    // 确认完成备料
    const confirmCompletePreparation = async () => {
      if (!currentOrderId.value) return;
      
      try {
        // 调用API更新状态，并传递实际备料数量
        const response = await updateMaterialPreparationStatus(currentOrderId.value, 4, actualQuantity.value);
        if (response.code === 200) {
          toast.success('备料完成');
          closeQuantityModal();
          fetchOrders();
        } else {
          toast.error('备料完成失败：' + (response.msg || '未知错误'));
        }
      } catch (error) {
        console.error('完成备料异常:', error);
        toast.error('系统错误，请重试');
      }
    }
    
    // 完成备料（修改为显示弹窗）
    const completePreparation = (order) => {
      openQuantityModal(order);
    }

    // 取消备料（不变）
    const cancelPreparation = async (id) => {
      if (confirm('确定要取消当前备料吗？')) {
        try {
          const response = await updateMaterialPreparationStatus(id, 6);
          if (response.code === 200) {
            toast.success('备料已取消');
            fetchOrders();
          } else {
            toast.error('取消备料失败：' + (response.msg || '未知错误'));
          }
        } catch (error) {
          console.error('取消备料异常:', error);
          toast.error('系统错误，请重试');
        }
      }
    }
    
    // 分配人员相关方法
    // 打开分配人员弹窗
    const openDistributeModal = async (order) => {
      currentDistributeOrder.value = order;
      selectedUserId.value = null;
      
      // 获取用户列表
      await fetchUsersList();
      
      showDistributeModal.value = true;
    }
    
    // 关闭分配人员弹窗
    const closeDistributeModal = () => {
      showDistributeModal.value = false;
      currentDistributeOrder.value = null;
      users.value = [];
      selectedUserId.value = null;
    }
    
    // 获取用户列表
    const fetchUsersList = async () => {
      loadingUsers.value = true;
      try {
        const response = await getUsersList({ current: 1, size: 20 });
        if (response.code === 200 && response.data) {
          users.value = Array.isArray(response.data.records) ? response.data.records : [];
        } else {
          users.value = [];
          toast.error('获取人员列表失败');
        }
      } catch (error) {
        console.error('获取人员列表异常:', error);
        toast.error('系统错误，请重试');
        users.value = [];
      } finally {
        loadingUsers.value = false;
      }
    }
    
    // 选择用户
    const selectUser = (userId) => {
      selectedUserId.value = userId;
    }
    
    // 确认分配
    const confirmDistribute = async () => {
      if (!currentDistributeOrder.value || !selectedUserId.value) return;
      
      try {
        // 调用API分配任务
        const response = await distributePreparationTask(currentDistributeOrder.value.id, selectedUserId.value);
        if (response.code === 200) {
          toast.success('分配成功');
          closeDistributeModal();
          fetchOrders();
        } else {
          toast.error('分配失败：' + (response.msg || '未知错误'));
        }
      } catch (error) {
        console.error('分配异常:', error);
        toast.error('系统错误，请重试');
      }
    }

    // 返回上一页（不变）
    const goBack = () => {
      router.back();
    }

    // 获取状态文本（不变）
    const getStatusText = (status) => {
      const statusMap = {
        1: '待分配',
        2: '分配中',
        3: '备料中',
        4: '完成',
        5: '已出库',
        6: '已取消'
      };
      return statusMap[status] || '未知状态';
    }

    // 获取紧急程度文本（不变）
    const getEmergencyLevelText = (level) => {
      const levelMap = {
        1: '正常',
        2: '紧急',
        3: '特急'
      };
      return levelMap[level] || '未知';
    }
    
    // 获取搜索占位符
    const getSearchPlaceholder = () => {
      const placeholderMap = {
        orderNo: '搜索备料单号...',
        supplierName: '搜索供应商...',
        materialNo: '搜索物料编码...'
      };
      return placeholderMap[searchType.value] || '搜索...';
    }

    // 页面加载时，重置声音开关状态
    onMounted(() => {
      // 重置声音开关为默认状态（关闭）
      isSoundEnabled.value = false;
      // 清空localStorage中的声音状态，确保下次刷新也会重置
      localStorage.removeItem('isSoundEnabled');
      
      document.addEventListener('click', handleGlobalInteraction);
      document.addEventListener('touchstart', handleGlobalInteraction);
      
      fetchOrders();
      initWebSocket();
    })

    // 页面卸载清理（不变）
    onUnmounted(() => {
      if (ws.value && isWsConnected.value) {
        ws.value.close();
        console.log('WebSocket 连接关闭');
      }
      window.speechSynthesis.cancel();
      document.removeEventListener('click', handleGlobalInteraction);
      document.removeEventListener('touchstart', handleGlobalInteraction);
    })

    return {
      orders,
      loading,
      searchKeyword,
      searchType,
      expandedOrderId,
      activeFilter,
      activeEmergencyFilter,
      filterOptions,
      emergencyFilterOptions,
      currentPage,
      pageSize,
      total,
      totalPages,
      showNotice,
      noticeData,
      isAudioInited,
      isSoundEnabled,
      // 弹窗相关数据
      showQuantityModal,
      currentOrderId,
      selectedOrder,
      actualQuantity,
      canModifyQuantity,
      // 分配人员相关数据
      showDistributeModal,
      currentDistributeOrder,
      users,
      loadingUsers,
      selectedUserId,
      // 弹窗相关方法
      openQuantityModal,
      closeQuantityModal,
      confirmCompletePreparation,
      // 分配人员相关方法
      openDistributeModal,
      closeDistributeModal,
      fetchUsersList,
      selectUser,
      confirmDistribute,
      setEmergencyFilter,
      handlePageChange,
      fetchOrders,
      searchOrders,
      getSearchPlaceholder,
      setFilter,
      toggleOrderDetail,
      canOperate,
      startPreparation,
      completePreparation,
      cancelPreparation,
      goBack,
      getStatusText,
      getEmergencyLevelText,
      toggleSound
    }
  }
}
</script>

<style scoped>
/* 分配人员弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.quantity-modal, .distribute-modal {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

/* 按钮样式 */
.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-primary {
  background-color: #23b1c0;
  color: white;
}

.btn-primary:hover {
  background-color: #1fa7b3;
}

.btn-success {
  height: 60px;
  background-color: #66bb6a;
  color: white;
}

.btn-success:hover {
  background-color: #5cae61;
}

.btn-danger {
  background-color: #e57373;
  color: white;
}

.btn-danger:hover {
  background-color: #ef5350;
}

.btn-default {
  background-color: #e0e0e0;
  color: #333;
}

.btn-default:hover {
  background-color: #bdbdbd;
}

.btn-info {
  background-color: #2196f3;
  color: white;
}

.btn-info:hover {
  background-color: #1e88e5;
}

/* 用户列表样式 */
.users-list {
  margin-top: 20px;
}

.users-list h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 15px;
}

.user-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border: 1px solid #eee;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.user-item:hover {
  background-color: #f5f5f5;
  border-color: #ddd;
}

.user-item.selected {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.user-name {
  font-weight: 500;
  color: #333;
}

/* 基础样式 */
.material-preparation-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
  margin: -15px -15px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #23b1c0;
  color: white;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 60px;
  box-sizing: border-box;
}

.back-button {
  cursor: pointer;
  padding: 5px;
}

.back-icon {
  width: 24px;
  height: 24px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

/* 声音开关样式 */
.header-right {
  width: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sound-toggle {
  cursor: pointer;
  font-size: 22px;
  transition: transform 0.2s ease;
}

.sound-toggle:hover {
  transform: scale(1.1);
}

.content {
  flex: 1;
  padding: 80px 15px 20px;
}

/* 音频权限提示样式 */
.audio-permission-tip {
  background: #fff8e1;
  color: #ff9800;
  padding: 10px 16px;
  border-radius: 8px;
  text-align: center;
  margin: 0 0 16px;
  cursor: pointer;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* 备料单列表样式 */
.order-list {
  background-color: white;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-group {
  display: flex;
  gap: 10px;
  margin-right: 15px;
  align-items: center;
}

.filter-nav {
  display: flex;
  border-radius: 4px;
  overflow: hidden;
}

.filter-item {
  flex: 1;
  padding: 8px 0;
  text-align: center;
  background-color: #f0f0f0;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.3s;
  border-right: 1px solid #ddd;
}

.filter-item:last-child {
  border-right: none;
}.filter-item.active {
  background-color: #23b1c0;
  color: white;
  font-weight: 600;
}

/* 搜索组件样式 */
.search-container {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 10px;
}

.search-type-select {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px 0 0 6px;
  background-color: white;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
  border-right: none;
}

.search-type-select:focus {
  border-color: #23b1c0;
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.1);
}

.search-input {
  height: 40px;
  padding: 0 16px;
  border: 1px solid #d0d0d0;
  border-radius: 0;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 200px;
}

.search-input:focus {
  border-color: #23b1c0;
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.1);
}

.search-btn {
  height: 35px;
  padding: 0 24px;
  border: none;
  border-radius: 0 6px 6px 0;
  background-color: #23b1c0;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background-color: #1a909c;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.2);
}

.search-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(35, 177, 192, 0.2);
}

/* 移动端响应式样式 */
@media (max-width: 768px) {
  .search-container {
    flex-direction: row;
    gap: 4px;
    margin-top: 8px;
    margin-bottom: 15px;
  }
  
  .search-type-select,
  .search-input,
  .search-btn {
    width: auto;
  }
  
  .search-type-select {
    border-radius: 6px 0 0 6px;
    border-right: none;
    padding: 0 8px;
  }
  
  .search-input {
    border-radius: 0;
    flex: 1;
    min-width: 100px;
    padding: 0 12px;
  }
  
  .search-btn {
    border-radius: 0 6px 6px 0;
    padding: 0 16px;
  }
}

.order-items /* 搜索组件样式 */
.search-container {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-bottom: 20px;
  margin-top: 10px;
}

.search-type-select {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d0d0d0;
  border-radius: 6px 0 0 6px;
  background-color: white;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
  border-right: none;
}

.search-type-select:focus {
  border-color: #23b1c0;
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.1);
}

.search-input {
  height: 40px;
  padding: 0 16px;
  border: 1px solid #d0d0d0;
  border-radius: 0;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 200px;
}

.search-input:focus {
  border-color: #23b1c0;
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.1);
}

.search-btn {
  height: 40px;
  padding: 0 24px;
  border: none;
  border-radius: 0 6px 6px 0;
  background-color: #23b1c0;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background-color: #1a909c;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(35, 177, 192, 0.2);
}

.search-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(35, 177, 192, 0.2);
}

/* 移动端响应式样式 */
@media (max-width: 768px) {
  .search-container {
    flex-direction: row;
    gap: 4px;
    margin-top: 8px;
    margin-bottom: 15px;
  }
  
  .search-type-select,
  .search-input,
  .search-btn {
    width: auto;
  }
  
  .search-type-select {
    border-radius: 6px 0 0 6px;
    border-right: none;
    padding: 0 8px;
  }
  
  .search-input {
    border-radius: 0;
    flex: 1;
    min-width: 100px;
    padding: 0 12px;
  }
  
  .search-btn {
    border-radius: 0 6px 6px 0;
    padding: 0 16px;
  }
}

.order-items {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.order-item {
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.order-item:hover {
  background-color: #f9f9f9;
  border-color: #ddd;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.order-number {
  font-weight: 600;
  color: #333;
  font-size: 15px;
  white-space: nowrap;    /* 强制不换行 */
  overflow: hidden;      /* 隐藏超出部分 */
  text-overflow: ellipsis; /* 超出部分显示省略号 */
}

/* 状态标签样式 */
.order-status {
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 14px;
  font-weight: 500;
  color: white;
}

.status-1 { background-color: #b0bec5; }
.status-2 { background-color: #78909c; }
.status-3 { background-color: #4fc3f7; }
.status-4 { background-color: #66bb6a; }
.status-5 { background-color: #9575cd; }
.status-6 { background-color: #e57373; }

/* 紧急程度样式 */
.emergency-badge.emergency-1 { color: #66bb6a; font-weight: 600; }
.emergency-badge.emergency-2 { color: #ff9800; font-weight: 600; }
.emergency-badge.emergency-3 { color: #f44336; font-weight: 600; }

.order-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.info-row .info-item {
  display: flex;
  align-items: center;
}

.info-row .info-item.left {
  justify-content: flex-start;
  flex: 1;
}

.info-row .info-item.right {
  justify-content: flex-end;
  margin-right: 30px;
  color: red;
  flex: 1;
}

.info-item .label {
  color: #666;
  font-size: 13px;
  margin-right: 4px;
}

.info-item .value {
  color: #333;
  font-weight: 500;
  font-size: 13px;
}

.expand-icon {
  position: absolute;
  right: 15px;
  bottom: 15px;
  transition: transform 0.3s;
  color: #666;
  font-size: 12px;
}

.expand-icon.expanded {
  transform: rotate(180deg);
}

.order-detail {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #eee;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.detail-section {
  margin-bottom: 15px;
}

.detail-section h3 {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
}

.detail-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 15px;
}

.btn-row {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 10px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  flex: 1;
}

.btn-primary { background-color: #007bff; color: white; }
.btn-primary:hover { background-color: #0056b3; }
.btn-success { background-color: #28a745; color: white; }
.btn-success:hover { background-color: #1e7e34; }
.btn-danger { background-color: #dc3545; color: white; }
.btn-danger:hover { background-color: #c82333; }

/* 无数据/加载中样式 */
.no-data, .loading {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

/* 分页样式 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  font-size: 14px;
  color: #8c8c8c;
}

.pagination-controls {
  display: flex;
  gap: 8px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.page-btn:hover:not(:disabled) {
  color: #1890ff;
  border-color: #1890ff;
}

.page-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* 消息通知弹窗样式 */
.notice-popup {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 20px;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 9999;
  animation: mobileSlideIn 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28), fadeOut 0.3s ease-in 3s forwards;
  max-width: 80%;
  box-sizing: border-box;
}

/* 紧急程度样式优化：区分新任务（正常1）和普通正常 */
.notice-level-1 { 
  border-left: 4px solid #4299e1; /* 蓝色标识新任务 */
  background-color: #e8f4f8; /* 浅蓝色背景增强感知 */
}
.notice-level-2 { 
  border-left: 4px solid #ff9800; 
  background-color: #fff8e1;
}
.notice-level-3 { 
  border-left: 4px solid #f44336; 
  background-color: #ffebee;
}

.notice-icon { 
  font-size: 20px;
  animation: pulse 0.6s ease-in-out;
}
.notice-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}
.notice-desc {
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; visibility: hidden; }
}

/* 移动端专用动画 */
@keyframes mobileSlideIn {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px) scale(0.9);
  }
  50% {
    transform: translateX(-50%) translateY(2px) scale(1.02);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

/* 图标脉冲动画 */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}

/* 响应式样式 */
@media (max-width: 768px) {
  .content { padding: 80px 12px 15px; }
  .order-list { padding: 12px; }
  .order-item { padding: 12px; }
  .action-buttons { flex-direction: column; }
  /* .btn-row 保持默认的 row 方向 */
  
  /* 移动端适配 */
  .filter-search-row {
    flex-direction: column;
    gap: 12px;
  }
  
  .filter-group {
    width: 100%;
    margin-right: 0;
    flex-direction: column;
    gap: 8px;
  }
  
  .filter-nav {
    width: 100%;
  }
  

}

/* 移动端弹窗优化 */
@media (max-width: 480px) {
  .material-preparation-container .notice-popup {
    top: 60px;
    padding: 14px 16px;
    max-width: 90%;
    width: 90%;
    margin: 0 5%;
    left: 50%;
    gap: 10px;
  }
  
  .material-preparation-container .notice-icon {
    font-size: 18px;
  }
  
  .material-preparation-container .notice-title {
    font-size: 14px;
    margin-bottom: 3px;
  }
  
  .material-preparation-container .notice-desc {
    font-size: 12px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
  }
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.quantity-modal {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 16px;
}

.order-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.info-item .label {
  color: #666;
  min-width: 100px;
}

.info-item .value {
  color: #333;
  font-weight: 500;
  flex: 1;
}

.quantity-input-group {
  margin-top: 8px;
}

.quantity-input {
  width: 100px;
  padding: 8px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 16px;
}

.quantity-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.warning-text {
  color: #ff6b6b;
  font-size: 12px;
  flex: 1;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
}

.modal-footer .btn {
  padding: 8px 20px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
}

.btn-default {
  background-color: #f0f0f0;
  border: 1px solid #d0d0d0;
  color: #333;
}

.btn-success {
  background-color: #4CAF50;
  border: 1px solid #4CAF50;
  color: white;
}
</style>